import dot from 'dot-prop-immutable';
import moment from 'moment';
import { denormalize } from 'normalizr';
import { any, path, pathOr, uniq, uniqBy } from 'ramda';
import reduceReducers from 'reduce-reducers';
import { createSelector } from 'reselect';

import type {
  ChatAnnotation,
  ChatChannel,
  ChatComment,
  ChatMscSpecialist,
  ChatShare,
} from '@frontend/api';

import { SUPPLIER_ROLES, PERMISSIONS } from 'constants/all';

import { createEntitiesReducer } from 'modules/common/redux';

import type { RootState } from 'store/configureStore';
import { getTeamMembers } from 'store/supplier/selectors';

import { toMoment } from 'utils/dateUtils';
import { createDeepMemoizedSelector } from 'utils/reduxUtils';

import { CHANNEL_TYPES } from '../data/constants';
import { getIsExportControlled } from '../data/reducer';
import type { ChatId } from '../data/types';
import { findFarthestExpiration } from '../shares/data/utils';
import { types, type ChatEntitiesAction } from './actions';
import { annotationSchema, channelSchema, shareSchema } from './entities';
import type {
  ActiveChannelUser,
  ChannelStreamItem,
  ChatEntities,
  NormalizedChatChannel,
} from './types';

type ChatEntitiesState = {
  /** empty after ENTITIES_STORE_INIT until the next action fills in the keys */
  entities: Partial<ChatEntities>;
};

/** `pathOr` defaults below are `[]`, which stands in for an empty map */
type NormalizedChannelMap = ChatEntities['channels'] | never[];

/** One empty map, so selecting no channels returns the same value each time. */
const EMPTY_CHANNELS: ChatEntities['channels'] = {};

const initialState: ChatEntitiesState = {
  entities: {},
};

const entitiesReducer = createEntitiesReducer<ChatEntitiesState>(
  ['annotations', 'channels', 'channelEvents', 'comments', 'shares', 'tasks'],
  [
    types.ANNOTATION_RECEIVE,
    types.CHANNEL_RECEIVE,
    types.CHANNEL_EVENT_RECEIVE,
    types.CHANNELS_RECEIVE,
    types.COMMENT_RECEIVE,
    types.SHARE_RECEIVE,
    types.TASK_RECEIVE,
  ],
);

const reducer = (
  state: ChatEntitiesState = initialState,
  action: ChatEntitiesAction,
): ChatEntitiesState => {
  switch (action.type) {
    case types.ANNOTATION_RECEIVE: {
      const { payload } = action;
      const { channelId } = payload.entities.annotations[payload.result];
      const nextAnnotationsForChannel = uniq(
        pathOr<number[]>(
          [],
          ['entities', 'channels', String(channelId), 'annotations'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.channels.${channelId}.annotations`,
        nextAnnotationsForChannel,
      );
    }
    case types.COMMENT_RECEIVE: {
      const { payload } = action;
      const { annotationId } = payload.entities.comments[payload.result];
      const nextCommentsListForAnnotation = uniq(
        pathOr<number[]>(
          [],
          ['entities', 'annotations', annotationId, 'comments'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.annotations.${annotationId}.comments`,
        nextCommentsListForAnnotation,
      );
    }
    case types.ENTITIES_STORE_INIT:
      return {
        ...initialState,
      };
    case types.SHARE_RECEIVE: {
      const { payload } = action;
      const { channelId } = payload.entities.shares[payload.result];
      const nextShareListForChannel = uniq(
        pathOr<number[]>(
          [],
          ['entities', 'channels', channelId, 'shares'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.channels.${channelId}.shares`,
        nextShareListForChannel,
      );
    }
    case types.CHANNEL_EVENT_RECEIVE: {
      const { payload } = action;
      const { channelId } = payload.entities.channelEvents[payload.result];
      const nextChannelEventListForChannel = uniq(
        pathOr<number[]>(
          [],
          ['entities', 'channels', String(channelId), 'channelEvents'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.channels.${channelId}.channelEvents`,
        nextChannelEventListForChannel,
      );
    }
    default:
      return state;
  }
};

const getScope = (state: RootState): ChatEntitiesState =>
  state.modules.common.chat.data;

export const getChannelById = (
  state: RootState,
  id: ChatId | null | undefined = null,
): ChatChannel | undefined => {
  if (id) {
    return denormalize(id, channelSchema, getScope(state).entities);
  }
};

export const channelHasActiveShares = (
  state: RootState,
  channelId: ChatId | null | undefined,
) => {
  const shares = pathOr<ChatShare[]>(
    [],
    ['shares'],
    getChannelById(state, channelId),
  );
  return shares.some((share) => share.accessRevokedOn == null);
};

export const getAnnotationById = (
  state: RootState,
  id: ChatId | null | undefined,
): ChatAnnotation | null | undefined =>
  denormalize(id, annotationSchema, getScope(state).entities);

export const getCommentById = (state: RootState, id: ChatId) =>
  pathOr<ChatComment | null>(null, ['comments', id], getScope(state).entities);

export const getChannelStream = createDeepMemoizedSelector(
  (state: RootState, id: ChatId | null | undefined) =>
    pathOr<ChatAnnotation[]>([], ['annotations'], getChannelById(state, id)),
  (state: RootState, id: ChatId | null | undefined) =>
    pathOr<ChatChannel['channelEvents']>(
      [],
      ['channelEvents'],
      getChannelById(state, id),
    ),
  (state: RootState, id: ChatId | null | undefined) =>
    pathOr<ChatShare[]>([], ['shares'], getChannelById(state, id)),
  (annotations, channelEvents, shares): ChannelStreamItem[] => {
    const revokedShares = shares
      .filter(
        (share): share is ChatShare & { accessRevokedOn: string } =>
          share.accessRevokedOn !== null,
      )
      .map((revokedShare) => ({
        ...revokedShare,
        isRevokedShare: true as const,
      }));
    const stream = (annotations as ChannelStreamItem[]).concat(
      shares,
      revokedShares,
      channelEvents,
    );
    return stream.sort((a, b) => {
      const aDate = a.isRevokedShare
        ? toMoment(a.accessRevokedOn)
        : toMoment(a.created);
      const bDate = b.isRevokedShare
        ? toMoment(b.accessRevokedOn)
        : toMoment(b.created);
      return aDate.valueOf() - bDate.valueOf();
    });
  },
);

export const getNormalizedSecondaryChannels = (state: RootState) =>
  Object.values(
    pathOr<ChatEntities['channels']>(
      {},
      ['entities', 'channels'],
      getScope(state),
    ),
  ).filter(({ type }) => type === CHANNEL_TYPES.SECONDARY);

export const getFirstSecondaryChannelId = (state: RootState) =>
  getNormalizedSecondaryChannels(state)[0]?.id;

/** Every channel chat holds, keyed by id. */
export const getNormalizedChannels = (state: RootState) =>
  pathOr<ChatEntities['channels']>(
    EMPTY_CHANNELS,
    ['entities', 'channels'],
    getScope(state),
  );

export const getNormalizedChannelById = (
  state: RootState,
  id: ChatId | null | undefined = null,
) => {
  if (id) {
    return pathOr<NormalizedChatChannel | null>(
      null,
      ['entities', 'channels', id],
      getScope(state),
    );
  }
};

// returns a bool map associating each user's id with their controlled access
const getTeamMembersCuiAccessMap = (state: RootState) => {
  const teamMembers = getTeamMembers(state);
  const accessMap: Record<string, boolean> = {};
  for (const teamMember of teamMembers) {
    const roles = teamMember?.supplierRoles;
    accessMap[teamMember.userId] = roles
      ? roles[SUPPLIER_ROLES.CONTROLLED_INFO] !== PERMISSIONS.NO_ACCESS
      : true;
  }
  return accessMap;
};

// returns an array of all team members or those who have access
// if channel is designated as controlled (CUI)
export const getTeamMembersForChannel = (state: RootState) => {
  const isChannelExportControlled = getIsExportControlled(state);
  const teamMemberAccessMap = getTeamMembersCuiAccessMap(state);
  if (!isChannelExportControlled) {
    return getTeamMembers(state);
  }

  return getTeamMembers(state).filter(
    (teamMember) => teamMemberAccessMap[teamMember.userId],
  );
};

// returns an array of all users who have commented in a channel or been @mentioned
// and have access if channel is designated as controlled (CUI)
export const getActiveUsersForChannel = (
  state: RootState,
  id: ChatId | null | undefined = null,
): ActiveChannelUser[] => {
  if (id) {
    // get all annotations belonging to this channel
    const annotationIds = pathOr<number[]>(
      [],
      ['annotations'],
      getNormalizedChannelById(state, id),
    );

    // if channel is controlled (CUI), exclude users who don't have access
    const isChannelExportControlled = getIsExportControlled(state);
    const teamMemberAccessMap = getTeamMembersCuiAccessMap(state);

    const comments = Object.values(
      pathOr<ChatEntities['comments']>(
        {},
        ['entities', 'comments'],
        getScope(state),
      ),
    ).filter(({ annotationId }) => annotationIds.includes(annotationId));

    const activeUsers: ActiveChannelUser[] = [];
    comments.forEach((comment) => {
      // add the comments author
      if (pathOr(true, ['isCreatedByActive'], comment)) {
        if (isChannelExportControlled) {
          // unless they have explicit NO ACCESS
          if (teamMemberAccessMap[String(comment.createdById)]) {
            activeUsers.push({
              fullName: comment.createdByFullName,
              userId: comment.createdById,
            });
          }
        } else {
          activeUsers.push({
            fullName: comment.createdByFullName,
            userId: comment.createdById,
          });
        }
      }
      // add @mentioned team mates
      comment.content.forEach((content) => {
        if (
          content.type === 'mention' &&
          pathOr(true, ['isMentionedUserActive'], content)
        ) {
          if (isChannelExportControlled) {
            // unless they have explicit NO ACCESS
            if (teamMemberAccessMap[String(content.userId)]) {
              activeUsers.push({
                fullName: content.content,
                userId: content.userId,
              });
            }
          } else {
            activeUsers.push({
              fullName: content.content,
              userId: content.userId,
            });
          }
        }
      });
    });

    return uniqBy(path(['userId']), activeUsers);
  }
  return [];
};

export const getAnnotationsCountForChannel = (
  state: RootState,
  channelId: ChatId | null | undefined = null,
) =>
  pathOr<number[]>(
    [],
    ['annotations'],
    getNormalizedChannelById(state, channelId),
  ).length;

export const getTotalAnnotationsCount = (state: RootState) =>
  Object.keys(pathOr({}, ['entities', 'annotations'], getScope(state))).length;

export const getInternalTeamChatChannelId = (state: RootState) => {
  const internalTeamChatChannel = Object.values(
    pathOr<ChatEntities['channels']>(
      {},
      ['entities', 'channels'],
      getScope(state),
    ),
  ).find(({ type }) => type == CHANNEL_TYPES.PRIMARY);

  return pathOr<number | null>(null, ['id'], internalTeamChatChannel);
};

export const getReqsReviewChatChannelId = (state: RootState) => {
  const reqsReviewChatChannel = Object.values(
    pathOr<ChatEntities['channels']>(
      {},
      ['entities', 'channels'],
      getScope(state),
    ),
  ).find(({ type }) => type == CHANNEL_TYPES.ACTIONS);

  return pathOr<number | null>(null, ['id'], reqsReviewChatChannel);
};

export const getMSCChatChannelId = (state: RootState) => {
  const mscChannel = Object.values(
    pathOr<ChatEntities['channels']>(
      {},
      ['entities', 'channels'],
      getScope(state),
    ),
  ).find(({ type }) => type == CHANNEL_TYPES.MSC);

  return pathOr<number | null>(null, ['id'], mscChannel);
};

export const getMscSpecialistForMscChannel = (state: RootState) => {
  const mscChannelId = getMSCChatChannelId(state);
  const mscChannel = getNormalizedChannelById(state, mscChannelId);
  return pathOr<Partial<ChatMscSpecialist>>({}, ['mscSpecialist'], mscChannel);
};

export const getPennChannelId = (state: RootState) => {
  const pennChannel = Object.values(
    pathOr<ChatEntities['channels']>(
      {},
      ['entities', 'channels'],
      getScope(state),
    ),
  ).find(({ type }) => type == CHANNEL_TYPES.PENN);

  return pathOr<number | null>(null, ['id'], pennChannel);
};

export const sharedUserHasPermissionToUploadFiles = (state: RootState) => {
  const normalizedChannels = pathOr<NormalizedChannelMap>(
    [],
    ['entities', 'channels'],
    getScope(state),
  );
  return Boolean(
    Object.values(normalizedChannels).some(
      (channel) => channel.permissionToUploadFiles,
    ),
  );
};

export const sharedUserHasPermissionToDownloadFiles = (state: RootState) => {
  const normalizedChannels = pathOr<NormalizedChannelMap>(
    [],
    ['entities', 'channels'],
    getScope(state),
  );
  return Boolean(
    Object.values(normalizedChannels).some(
      (channel) => channel.permissionToDownloadFiles,
    ),
  );
};

export const getSharedUserHasAccessToPrimaryFile = createSelector(
  getNormalizedChannels,
  (normalizedChannels) =>
    Boolean(
      Object.values(normalizedChannels).some(
        (channel) => channel.accessToPrimaryFile,
      ),
    ),
);

const getSharesByChannelId = (
  state: RootState,
  channelId: ChatId,
): ChatShare[] | null => {
  const normalizedChannels = pathOr<NormalizedChannelMap>(
    [],
    ['entities', 'channels'],
    getScope(state),
  );

  if (!(channelId in normalizedChannels)) {
    return null;
  }

  // denormalizing a single (non-array) value against `[channelSchema]` returns
  // it as-is, so this is still the normalized channel with share ids
  const denormalizedChannel: NormalizedChatChannel = denormalize(
    (normalizedChannels as ChatEntities['channels'])[channelId],
    [channelSchema],
    getScope(state).entities,
  );
  const normalizedShares = pathOr<number[]>(
    [],
    ['shares'],
    denormalizedChannel,
  );
  const denormalizedShares: ChatShare[] = denormalize(
    normalizedShares,
    [shareSchema],
    getScope(state).entities,
  );

  return denormalizedShares;
};

export const getShareExpirationByChannelIdForGroup = (
  state: RootState,
  channelId: ChatId,
  groupId: number | null | undefined,
) => {
  const denormalizedShares = getSharesByChannelId(state, channelId);
  if (!denormalizedShares) {
    return null;
  }

  const relevantShareExpirationDates = denormalizedShares
    .filter(
      (share) =>
        share.accessRevokedOn === null && share.sharedWithGroupId === groupId,
    )
    .map((share) => share.expirationDate);

  if (relevantShareExpirationDates.length === 0) {
    return null;
  }

  return findFarthestExpiration(relevantShareExpirationDates);
};

export const isAnyShareToActiveGroupValid = (
  state: RootState,
  groupId: number | null | undefined,
) => {
  const shares = Object.values(
    pathOr<ChatEntities['shares'] | never[]>(
      [],
      ['entities', 'shares'],
      getScope(state),
    ),
  );

  const relevantShares = shares.filter(
    (share) =>
      share.accessRevokedOn === null && share.sharedWithGroupId === groupId,
  );
  const expirationDates = relevantShares.map((share) =>
    share.expirationDate
      ? toMoment(share.expirationDate)
      : share.expirationDate,
  );
  const now = moment();

  return any(
    (expirationDate) => !expirationDate || now.isBefore(expirationDate),
  )(expirationDates);
};

export const getFarthestExpirationForChannel = (
  state: RootState,
  channelId: ChatId,
) => {
  const denormalizedShares = getSharesByChannelId(state, channelId);
  if (!denormalizedShares) {
    return null;
  }

  const relevantShareExpirationDates = denormalizedShares
    .filter((share) => share.accessRevokedOn === null)
    .map((share) => share.expirationDate);

  if (relevantShareExpirationDates.length === 0) {
    return null;
  }

  return findFarthestExpiration(relevantShareExpirationDates);
};

export const isChannelAcessExpired = (state: RootState, channelId: ChatId) => {
  const channelExpiration = getFarthestExpirationForChannel(state, channelId);
  return channelExpiration != null && Date.now() > channelExpiration.valueOf();
};

export default reduceReducers(entitiesReducer, reducer, initialState);
